// Manager admin: gestão de Grupos de Estudo (peer-led).
// Diferente de GruposView (que mexe em `groups` — turma docente).
//
// Funcionalidades:
//  - Lista de todos os grupos com counts (membros, bancos, pastas)
//  - Criar grupo (admin vira owner automático)
//  - Editar (nome, descrição, privacidade) via modal com tabs:
//      * Geral: nome/descrição/privacidade
//      * Membros: lista + adicionar por email + promover/rebaixar/remover
//      * Bancos: lista + adicionar do catálogo + remover
//  - Deletar (cascade — apaga members + items + invites)

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  ChevronRight,
  Crown,
  Database,
  Eye,
  EyeOff,
  File as FileIcon,
  Folder,
  FolderPlus,
  Loader2,
  Move,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserPlus,
  Users,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, type BankListItem } from "./api";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

type StudyGroupListItem = Awaited<
  ReturnType<typeof api.listStudyGroups>
>["groups"][number];

type StudyGroupDetailResp = Awaited<ReturnType<typeof api.getStudyGroup>>;

const phase = signal<"loading" | "ready" | "error">("loading");
const list = signal<StudyGroupListItem[]>([]);
const allBanks = signal<BankListItem[]>([]);
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const editingId = signal<string | null>(null);
const creating = signal(false);

async function load(opts: { silent?: boolean } = {}) {
  if (!opts.silent) phase.value = "loading";
  errMsg.value = null;
  try {
    const [sg, b] = await Promise.all([
      api.listStudyGroups(),
      api.listBanks()
    ]);
    list.value = sg.groups;
    allBanks.value = b.banks;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

export function GruposEstudoView() {
  useEffect(() => {
    void load();
  }, []);

  const term = search.value.trim().toLowerCase();
  const filtered = term
    ? list.value.filter((g) =>
        `${g.name} ${g.description ?? ""} ${g.createdByName ?? ""}`
          .toLowerCase()
          .includes(term)
      )
    : list.value;

  return (
    <div class="space-y-4">
      <header class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start gap-3">
          <span class="grid size-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-md">
            <Users className="size-5" />
          </span>
          <div>
            <h1 class="text-2xl font-semibold tracking-tight">
              Grupos de Estudo
            </h1>
            <p class="mt-0.5 text-sm text-muted-foreground">
              Gestão cross-org dos grupos peer-led criados pelos alunos. Admin
              pode adicionar bancos, gerenciar membros e privacidade.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700"
        >
          <Plus className="size-4" />
          Novo grupo
        </button>
      </header>

      <div class="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5">
        <Search className="size-3.5 text-muted-foreground" />
        <input
          type="search"
          placeholder="Buscar por nome, descrição ou criador…"
          value={search.value}
          onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
          class="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        <span class="shrink-0 text-xs text-muted-foreground">
          {filtered.length}/{list.value.length}
        </span>
      </div>

      {phase.value === "loading" && (
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton class="h-32 w-full" />
          <Skeleton class="h-32 w-full" />
          <Skeleton class="h-32 w-full" />
        </div>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
          {errMsg.value ?? "Erro ao carregar grupos."}
        </div>
      )}

      {phase.value === "ready" && filtered.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
          {term ? "Nenhum grupo bate com a busca." : "Nenhum grupo cadastrado ainda."}
        </div>
      )}

      {phase.value === "ready" && filtered.length > 0 && (
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => (editingId.value = g.id)}
              class="group rounded-lg border border-border bg-card p-3 text-left transition hover:-translate-y-0.5 hover:border-violet-500/60 hover:shadow-md"
            >
              <div class="flex items-start gap-2.5">
                <span class="grid size-10 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 text-sm font-bold text-white">
                  {initials(g.name)}
                </span>
                <div class="min-w-0 flex-1">
                  <h3 class="line-clamp-1 text-sm font-semibold">{g.name}</h3>
                  {g.description && (
                    <p class="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
                      {g.description}
                    </p>
                  )}
                  <p class="mt-0.5 text-[10px] text-muted-foreground/70">
                    criado por {g.createdByName ?? "—"}
                  </p>
                </div>
                {g.hideMemberInfo && (
                  <EyeOff
                    className="size-3.5 text-amber-500"
                    title="Privacidade ligada"
                  />
                )}
              </div>
              <div class="mt-3 flex items-center gap-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
                <span class="inline-flex items-center gap-1">
                  <Users className="size-3" />
                  <strong class="text-foreground">{g.memberCount}</strong>
                  membros
                </span>
                <span class="inline-flex items-center gap-1">
                  <Database className="size-3" />
                  <strong class="text-foreground">{g.itemCount}</strong>
                  bancos
                </span>
                {g.folderCount > 0 && (
                  <span class="inline-flex items-center gap-1">
                    <Folder className="size-3 text-amber-500" />
                    {g.folderCount}
                  </span>
                )}
                <Pencil className="ml-auto size-3 opacity-0 transition group-hover:opacity-100" />
              </div>
            </button>
          ))}
        </div>
      )}

      {creating.value && (
        <CreateModal
          onClose={() => (creating.value = false)}
          onCreated={(id) => {
            creating.value = false;
            void load({ silent: true });
            editingId.value = id;
          }}
        />
      )}
      {editingId.value && (
        <EditModal
          groupId={editingId.value}
          onClose={() => (editingId.value = null)}
          onChanged={() => void load({ silent: true })}
        />
      )}
    </div>
  );
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const numTokens = words.filter((w) => /^\d+$/.test(w));
  const alphaTokens = words.filter((w) => /[a-zA-ZÀ-ÿ]/.test(w));
  if (numTokens.length > 0 && alphaTokens.length > 0) {
    const num = numTokens[numTokens.length - 1];
    return `${alphaTokens[0][0].toUpperCase()}${num.length <= 3 ? num : num.slice(-2)}`;
  }
  if (alphaTokens.length === 1) return alphaTokens[0].slice(0, 2).toUpperCase();
  if (alphaTokens.length >= 2)
    return (alphaTokens[0][0] + alphaTokens[alphaTokens.length - 1][0]).toUpperCase();
  return (words[0] ?? "?").slice(0, 2).toUpperCase();
}

// ============================================================================
// Create
// ============================================================================

function CreateModal({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hideMemberInfo, setHideMemberInfo] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await api.createStudyGroup({
        name: name.trim(),
        description: description.trim() || null,
        hideMemberInfo
      });
      onCreated(r.group.id);
    } catch (err) {
      void openAlert({
        title: "Não consegui criar",
        message: friendlyError(err)
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Novo Grupo de Estudo" onClose={onClose}>
      <div class="space-y-4">
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Nome
          </label>
          <input
            type="text"
            maxLength={80}
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="Ex.: MED Unicid Turma 35"
            class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            autoFocus
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Descrição (opcional)
          </label>
          <textarea
            maxLength={500}
            rows={3}
            value={description}
            onInput={(e) =>
              setDescription((e.target as HTMLTextAreaElement).value)
            }
            placeholder="Grupo de estudo da turma…"
            class="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
          />
        </div>
        <label class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2 text-xs">
          <span class="flex items-start gap-2">
            <EyeOff className="mt-0.5 size-4 text-amber-500" />
            <span>
              <strong class="block text-foreground">
                Ocultar nome/email entre membros
              </strong>
              <span class="text-muted-foreground">
                Não-owners veem apenas iniciais. Pode mudar depois.
              </span>
            </span>
          </span>
          <Switch
            checked={hideMemberInfo}
            onCheckedChange={(v) => setHideMemberInfo(v)}
          />
        </label>
      </div>
      <div class="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          class="rounded-md border border-border bg-card px-3 py-2 text-sm hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}
          Criar
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Edit (com tabs)
// ============================================================================

function EditModal({
  groupId,
  onClose,
  onChanged
}: {
  groupId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<StudyGroupDetailResp | null>(null);
  const [tab, setTab] = useState<"general" | "members" | "repo">("general");
  const [loading, setLoading] = useState(true);

  async function reload() {
    setLoading(true);
    try {
      const r = await api.getStudyGroup(groupId);
      setDetail(r);
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err) });
      onClose();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [groupId]);

  async function deleteGroup() {
    if (!detail) return;
    const ok = await openConfirm({
      title: "Apagar grupo",
      message: `Apagar "${detail.group.name}"? Membros, bancos e convites serão removidos.`,
      confirmLabel: "Apagar",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteStudyGroup(groupId);
      onChanged();
      onClose();
    } catch (err) {
      void openAlert({
        title: "Não consegui apagar",
        message: friendlyError(err)
      });
    }
  }

  return (
    <ModalShell
      title={detail?.group.name ?? "Carregando…"}
      onClose={onClose}
      size="lg"
    >
      {loading || !detail ? (
        <div class="flex items-center justify-center py-10">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div class="-mt-2 mb-3 flex gap-1 border-b border-border text-xs">
            <TabBtn active={tab === "general"} onClick={() => setTab("general")}>
              Geral
            </TabBtn>
            <TabBtn active={tab === "members"} onClick={() => setTab("members")}>
              Membros ({detail.members.length})
            </TabBtn>
            <TabBtn active={tab === "repo"} onClick={() => setTab("repo")}>
              Repositório (
              {detail.items.filter((i) => i.kind !== "folder").length})
            </TabBtn>
          </div>
          {tab === "general" && (
            <GeneralTab
              detail={detail}
              onSaved={async () => {
                await reload();
                onChanged();
              }}
            />
          )}
          {tab === "members" && (
            <MembersTab
              detail={detail}
              onChanged={async () => {
                await reload();
                onChanged();
              }}
            />
          )}
          {tab === "repo" && (
            <RepositorioTab
              detail={detail}
              onChanged={async () => {
                await reload();
                onChanged();
              }}
            />
          )}
          <div class="mt-5 flex justify-between gap-2 border-t border-border pt-3">
            <button
              type="button"
              onClick={() => void deleteGroup()}
              class="inline-flex items-center gap-1 rounded-md text-xs font-medium text-red-600 hover:underline dark:text-red-400"
            >
              <Trash2 className="size-3.5" />
              Apagar grupo
            </button>
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-card px-3 py-1.5 text-sm hover:bg-accent"
            >
              Fechar
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

function GeneralTab({
  detail,
  onSaved
}: {
  detail: StudyGroupDetailResp;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(detail.group.name);
  const [description, setDescription] = useState(
    detail.group.description ?? ""
  );
  const [hideMemberInfo, setHideMemberInfo] = useState(
    detail.group.hideMemberInfo
  );
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.patchStudyGroup(detail.group.id, {
        name: name.trim(),
        description: description.trim() || null,
        hideMemberInfo
      });
      await onSaved();
    } catch (err) {
      void openAlert({
        title: "Não consegui salvar",
        message: friendlyError(err)
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="space-y-4">
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Nome
        </label>
        <input
          type="text"
          maxLength={80}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
        />
      </div>
      <div>
        <label class="mb-1 block text-xs font-medium text-muted-foreground">
          Descrição
        </label>
        <textarea
          maxLength={500}
          rows={3}
          value={description}
          onInput={(e) =>
            setDescription((e.target as HTMLTextAreaElement).value)
          }
          class="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
        />
      </div>
      <label class="flex items-center justify-between gap-3 rounded-md border border-border bg-card/40 px-3 py-2 text-xs">
        <span class="flex items-start gap-2">
          {hideMemberInfo ? (
            <EyeOff className="mt-0.5 size-4 text-amber-500" />
          ) : (
            <Eye className="mt-0.5 size-4 text-violet-500" />
          )}
          <span>
            <strong class="block text-foreground">
              Privacidade dos membros
            </strong>
            <span class="text-muted-foreground">
              {hideMemberInfo
                ? "Membros não-proprietários veem apenas iniciais."
                : "Todos os membros veem nome e email uns dos outros."}
            </span>
          </span>
        </span>
        <Switch
          checked={hideMemberInfo}
          onCheckedChange={(v) => setHideMemberInfo(v)}
        />
      </label>
      <div class="text-[11px] text-muted-foreground">
        Criado em{" "}
        {new Date(detail.group.createdAt).toLocaleDateString("pt-BR")} ·
        última atualização{" "}
        {new Date(detail.group.updatedAt).toLocaleDateString("pt-BR")}
      </div>
      <div class="flex justify-end">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !name.trim()}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          Salvar mudanças
        </button>
      </div>
    </div>
  );
}

function MembersTab({
  detail,
  onChanged
}: {
  detail: StudyGroupDetailResp;
  onChanged: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [newRole, setNewRole] = useState<"member" | "owner">("member");
  const [adding, setAdding] = useState(false);

  async function addByEmail() {
    if (!email.trim()) return;
    setAdding(true);
    try {
      await api.studyGroupAddMember(detail.group.id, {
        email: email.trim(),
        role: newRole
      });
      setEmail("");
      setNewRole("member");
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui adicionar",
        message: friendlyError(err)
      });
    } finally {
      setAdding(false);
    }
  }

  async function changeRole(userId: string, role: "owner" | "member") {
    try {
      await api.studyGroupSetRole(detail.group.id, userId, role);
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui alterar",
        message: friendlyError(err)
      });
    }
  }

  async function removeMember(userId: string, name: string) {
    const ok = await openConfirm({
      title: "Remover membro",
      message: `Remover ${name} do grupo?`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      await api.studyGroupRemoveMember(detail.group.id, userId);
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui remover",
        message: friendlyError(err)
      });
    }
  }

  return (
    <div class="space-y-3">
      <div class="space-y-2 rounded-md border border-border bg-card/40 p-2.5">
        <div class="flex gap-2">
          <input
            type="email"
            placeholder="email@exemplo.com"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            disabled={adding}
            onKeyDown={(e) => {
              if (e.key === "Enter") void addByEmail();
            }}
            class="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void addByEmail()}
            disabled={adding || !email.trim()}
            class="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {adding ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <UserPlus className="size-4" />
            )}
            Adicionar
          </button>
        </div>
        <div class="flex items-center gap-2 text-[11px]">
          <span class="text-muted-foreground">Adicionar como:</span>
          <button
            type="button"
            onClick={() => setNewRole("member")}
            class={`rounded-md px-2 py-1 font-medium transition ${
              newRole === "member"
                ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            Membro
          </button>
          <button
            type="button"
            onClick={() => setNewRole("owner")}
            class={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium transition ${
              newRole === "owner"
                ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                : "text-muted-foreground hover:bg-accent"
            }`}
          >
            <Crown className="size-3" />
            Proprietário
          </button>
          <span class="ml-auto text-muted-foreground/70">
            Bypass de convite — entra direto.
          </span>
        </div>
      </div>

      <div class="max-h-[40vh] overflow-y-auto rounded-md border border-border bg-card">
        {detail.members.map((m, idx) => {
          const isLast = idx === detail.members.length - 1;
          return (
            <div
              key={m.userId}
              class={`flex items-center justify-between gap-2 px-3 py-2 text-sm ${
                isLast ? "" : "border-b border-border"
              }`}
            >
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1.5">
                  <span class="truncate font-medium">{m.name}</span>
                  {m.role === "owner" && (
                    <span class="inline-flex items-center gap-0.5 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                      <Crown className="size-2.5" />
                      Owner
                    </span>
                  )}
                </div>
                <div class="truncate text-[11px] text-muted-foreground">
                  {m.email}
                </div>
              </div>
              <div class="flex shrink-0 gap-1">
                {m.role === "member" ? (
                  <button
                    type="button"
                    onClick={() => void changeRole(m.userId, "owner")}
                    title="Promover a proprietário"
                    class="rounded-md px-2 py-1 text-[11px] hover:bg-accent"
                  >
                    <Crown className="size-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void changeRole(m.userId, "member")}
                    title="Rebaixar a membro"
                    class="rounded-md px-2 py-1 text-[11px] hover:bg-accent"
                  >
                    Rebaixar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void removeMember(m.userId, m.name)}
                  title="Remover do grupo"
                  class="rounded-md px-2 py-1 text-[11px] text-red-600 hover:bg-red-500/10 dark:text-red-400"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type StudyGroupItem = StudyGroupDetailResp["items"][number];

function RepositorioTab({
  detail,
  onChanged
}: {
  detail: StudyGroupDetailResp;
  onChanged: () => Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [picker, setPicker] = useState(false);
  const [moving, setMoving] = useState<StudyGroupItem | null>(null);
  const banks = allBanks.value;
  const items = detail.items;
  const folders = items.filter((i) => i.kind === "folder");
  const currentBankIds = new Set(
    items.filter((i) => i.kind === "bank_ref").map((i) => i.bankId)
  );
  const visibleBanks = q.trim()
    ? banks.filter((b) => b.name.toLowerCase().includes(q.trim().toLowerCase()))
    : banks.slice(0, 100);

  // Indexa filhos por parentId (null = raiz)
  const byParent = new Map<string | null, StudyGroupItem[]>();
  for (const it of items) {
    const key = it.parentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(it);
  }
  // Ordena cada bucket: folders primeiro, depois por position
  for (const arr of byParent.values()) {
    arr.sort((a, b) => {
      if (a.kind === "folder" && b.kind !== "folder") return -1;
      if (a.kind !== "folder" && b.kind === "folder") return 1;
      return a.position - b.position;
    });
  }

  async function addBank(bankId: string) {
    try {
      await api.studyGroupAddBank(detail.group.id, bankId);
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui adicionar",
        message: friendlyError(err)
      });
    }
  }

  async function createFolder() {
    const name = await openPrompt({
      title: "Nova pasta",
      message: "Nome da pasta (criada na raiz):",
      placeholder: "Ex.: Provas 2024",
      maxLength: 120
    });
    if (!name || !name.trim()) return;
    try {
      await api.studyGroupCreateFolder(detail.group.id, {
        name: name.trim(),
        parentId: null
      });
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui criar",
        message: friendlyError(err)
      });
    }
  }

  async function renameItem(it: StudyGroupItem) {
    const name = await openPrompt({
      title: it.kind === "folder" ? "Renomear pasta" : "Renomear item",
      message: "Novo nome:",
      defaultValue: it.name,
      maxLength: 120
    });
    if (!name || !name.trim() || name.trim() === it.name) return;
    try {
      await api.studyGroupRenameItem(detail.group.id, it.id, name.trim());
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui renomear",
        message: friendlyError(err)
      });
    }
  }

  async function deleteItem(it: StudyGroupItem) {
    const isFolder = it.kind === "folder";
    const hasChildren = (byParent.get(it.id) ?? []).length > 0;
    const msg = isFolder
      ? hasChildren
        ? `Apagar pasta "${it.name}" E TUDO dentro dela (cascade)?`
        : `Apagar pasta vazia "${it.name}"?`
      : `Remover "${it.name}" do repositório do grupo?`;
    const ok = await openConfirm({
      title: isFolder ? "Apagar pasta" : "Remover item",
      message: msg,
      confirmLabel: isFolder ? "Apagar" : "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      await api.studyGroupDeleteItem(detail.group.id, it.id);
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui apagar",
        message: friendlyError(err)
      });
    }
  }

  async function moveTo(it: StudyGroupItem, targetParentId: string | null) {
    setMoving(null);
    if ((it.parentId ?? null) === targetParentId) return;
    try {
      await api.studyGroupMoveItem(detail.group.id, it.id, targetParentId);
      await onChanged();
    } catch (err) {
      void openAlert({
        title: "Não consegui mover",
        message: friendlyError(err)
      });
    }
  }

  // Lista de destinos válidos pra um item (exclui ele mesmo + descendentes se for folder)
  function validParents(it: StudyGroupItem): Array<{ id: string | null; name: string }> {
    const forbidden = new Set<string>([it.id]);
    if (it.kind === "folder") {
      // BFS descendentes
      const queue: string[] = [it.id];
      while (queue.length > 0) {
        const cur = queue.shift()!;
        for (const child of byParent.get(cur) ?? []) {
          if (child.kind === "folder" && !forbidden.has(child.id)) {
            forbidden.add(child.id);
            queue.push(child.id);
          }
        }
      }
    }
    const out: Array<{ id: string | null; name: string }> = [
      { id: null, name: "📁 Raiz" }
    ];
    for (const f of folders) {
      if (!forbidden.has(f.id)) out.push({ id: f.id, name: f.name });
    }
    return out;
  }

  return (
    <div class="space-y-3">
      <div class="flex items-center justify-between gap-2">
        <div class="text-[11px] text-muted-foreground">
          {folders.length} pasta(s) · {items.filter((i) => i.kind === "bank_ref").length} banco(s)
        </div>
        <div class="flex gap-2">
          <button
            type="button"
            onClick={() => void createFolder()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <FolderPlus className="size-3.5" />
            Nova pasta
          </button>
          <button
            type="button"
            onClick={() => setPicker(!picker)}
            class="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700"
          >
            <Plus className="size-3.5" />
            {picker ? "Fechar catálogo" : "Adicionar banco"}
          </button>
        </div>
      </div>

      {picker && (
        <div class="space-y-2 rounded-md border border-border bg-card p-3">
          <input
            type="search"
            placeholder="Buscar banco no catálogo…"
            value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs"
          />
          <div class="max-h-[30vh] overflow-y-auto rounded-md border border-border">
            {visibleBanks.map((b) => {
              const already = currentBankIds.has(b.id);
              return (
                <div
                  key={b.id}
                  class="flex items-center justify-between gap-2 border-b border-border px-3 py-1.5 text-xs last:border-0"
                >
                  <span class="min-w-0 truncate">{b.name}</span>
                  <button
                    type="button"
                    onClick={() => void addBank(b.id)}
                    disabled={already}
                    class="shrink-0 rounded-md bg-violet-500/10 px-2 py-1 text-[11px] font-medium text-violet-700 hover:bg-violet-500/20 disabled:opacity-50 dark:text-violet-300"
                  >
                    {already ? "Já adicionado" : "Adicionar"}
                  </button>
                </div>
              );
            })}
          </div>
          <p class="text-[10px] text-muted-foreground">
            Banco adicionado entra na raiz. Use o botão Mover pra colocar em pasta.
          </p>
        </div>
      )}

      <div class="max-h-[50vh] overflow-y-auto rounded-md border border-border bg-card">
        {items.length === 0 ? (
          <div class="p-6 text-center text-xs italic text-muted-foreground">
            Repositório vazio. Crie uma pasta ou adicione um banco.
          </div>
        ) : (
          <TreeNode
            parentId={null}
            depth={0}
            byParent={byParent}
            onRename={(it) => void renameItem(it)}
            onDelete={(it) => void deleteItem(it)}
            onMove={(it) => setMoving(it)}
          />
        )}
      </div>

      {moving && (
        <MoveItemModal
          item={moving}
          targets={validParents(moving)}
          onClose={() => setMoving(null)}
          onChoose={(pid) => void moveTo(moving, pid)}
        />
      )}
    </div>
  );
}

function TreeNode({
  parentId,
  depth,
  byParent,
  onRename,
  onDelete,
  onMove
}: {
  parentId: string | null;
  depth: number;
  byParent: Map<string | null, StudyGroupItem[]>;
  onRename: (it: StudyGroupItem) => void;
  onDelete: (it: StudyGroupItem) => void;
  onMove: (it: StudyGroupItem) => void;
}) {
  const children = byParent.get(parentId) ?? [];
  if (children.length === 0) return null;
  return (
    <>
      {children.map((it) => (
        <TreeRow
          key={it.id}
          item={it}
          depth={depth}
          byParent={byParent}
          onRename={onRename}
          onDelete={onDelete}
          onMove={onMove}
        />
      ))}
    </>
  );
}

function TreeRow({
  item,
  depth,
  byParent,
  onRename,
  onDelete,
  onMove
}: {
  item: StudyGroupItem;
  depth: number;
  byParent: Map<string | null, StudyGroupItem[]>;
  onRename: (it: StudyGroupItem) => void;
  onDelete: (it: StudyGroupItem) => void;
  onMove: (it: StudyGroupItem) => void;
}) {
  const isFolder = item.kind === "folder";
  const childCount = isFolder ? (byParent.get(item.id) ?? []).length : 0;
  const [open, setOpen] = useState(true);
  const Icon = isFolder
    ? Folder
    : item.kind === "bank_ref"
      ? Database
      : FileIcon;
  return (
    <>
      <div
        class="group flex items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-0"
        style={{ paddingLeft: `${12 + depth * 18}px` }}
      >
        {isFolder ? (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            class="text-muted-foreground hover:text-foreground"
            title={open ? "Recolher" : "Expandir"}
          >
            <ChevronRight
              className={`size-3.5 transition ${open ? "rotate-90" : ""}`}
            />
          </button>
        ) : (
          <span class="w-3.5" />
        )}
        <Icon
          className={`size-4 shrink-0 ${
            isFolder ? "text-amber-500" : "text-violet-500"
          }`}
        />
        <span class="min-w-0 flex-1 truncate">{item.name}</span>
        {isFolder && (
          <span class="shrink-0 text-[10px] text-muted-foreground">
            {childCount} {childCount === 1 ? "item" : "itens"}
          </span>
        )}
        <div class="flex shrink-0 gap-0.5 opacity-0 transition group-hover:opacity-100">
          <button
            type="button"
            onClick={() => onRename(item)}
            title="Renomear"
            class="rounded-md p-1 hover:bg-accent"
          >
            <Pencil className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => onMove(item)}
            title="Mover pra outra pasta"
            class="rounded-md p-1 hover:bg-accent"
          >
            <Move className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(item)}
            title={isFolder ? "Apagar pasta (cascade)" : "Remover"}
            class="rounded-md p-1 text-red-600 hover:bg-red-500/10 dark:text-red-400"
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      </div>
      {isFolder && open && (
        <TreeNode
          parentId={item.id}
          depth={depth + 1}
          byParent={byParent}
          onRename={onRename}
          onDelete={onDelete}
          onMove={onMove}
        />
      )}
    </>
  );
}

function MoveItemModal({
  item,
  targets,
  onClose,
  onChoose
}: {
  item: StudyGroupItem;
  targets: Array<{ id: string | null; name: string }>;
  onClose: () => void;
  onChoose: (parentId: string | null) => void;
}) {
  return (
    <div
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="w-full max-w-sm rounded-2xl bg-card p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-3 flex items-center justify-between">
          <h3 class="truncate text-sm font-semibold">
            Mover "{item.name}" para…
          </h3>
          <button
            type="button"
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <div class="max-h-[50vh] overflow-y-auto rounded-md border border-border">
          {targets.map((t) => {
            const isCurrent = (item.parentId ?? null) === t.id;
            return (
              <button
                key={t.id ?? "__root__"}
                type="button"
                disabled={isCurrent}
                onClick={() => onChoose(t.id)}
                class={`flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left text-xs last:border-0 ${
                  isCurrent
                    ? "opacity-50"
                    : "hover:bg-accent"
                }`}
              >
                <span class="truncate">{t.name}</span>
                {isCurrent && (
                  <span class="text-[10px] text-muted-foreground">atual</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Modal shell + tab button compartilhados
// ============================================================================

function ModalShell({
  title,
  children,
  onClose,
  size = "md"
}: {
  title: string;
  children: any;
  onClose: () => void;
  size?: "md" | "lg";
}) {
  const w = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        class={`w-full ${w} max-h-[90vh] overflow-y-auto rounded-t-2xl bg-card p-6 shadow-xl sm:rounded-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="truncate text-lg font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
            title="Fechar"
            aria-label="Fechar"
          >
            <X className="size-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: any;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`border-b-2 px-3 py-2 font-medium transition ${
        active
          ? "border-violet-500 text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
